import React, { MouseEventHandler, useMemo } from 'react';

import { prefillOrderInputPrice } from '#/reduxState/orders/prefillOrderInputs';
import { staticDispatch } from '#/reduxState/store';

import BidsAsks from './BidsAsks';
import { depthChartStyle, DEPTH_CHART_CLIP_AREA_ID } from './chartConfig';
import MarketDepthAxes from './MarketDepthAxes';
import MarketDepthTooltip from './MarketDepthTooltip';
import MarketDepthTooltipLine from './MarketDepthTooltipLine';
import { ChartMousePosition, ChartPoint, ScaleFn } from './types';
import {
  binarySearchAscending,
  binarySearchDescending,
  getCumulativePoints,
  head,
  last,
} from './utils';

function mousePositionCallback(
  event: React.MouseEvent<SVGSVGElement | HTMLDivElement, MouseEvent>
) {
  const container = event.currentTarget;
  const rect = container.getBoundingClientRect();
  const x = event.clientX - rect.left - container.clientLeft;
  const y = event.clientY - rect.top - container.clientTop;
  return [Math.round(x), Math.round(y)];
}

type MarketDepthChartCoreProps = {
  width: number;
  height: number;
  asks: [number, number][];
  bids: [number, number][];
  // bidsSum: [number, number][];
  // asksSum: [number, number][];
  baseSymbol: string;
  quoteSymbol: string;
  clickPrefillPrice: boolean;
};

type MarketDepthChartProps = MarketDepthChartCoreProps & {
  domain: { xMin: number; xMax: number; yMin: number; yMax: number };
  dimensions: { availableHeight: number; availableWidth: number };
  // xScale: ScaleFn;
  // yScale: ScaleFn;
  data: { bidPoints: ChartPoint[]; askPoints: ChartPoint[] };
  midPoint: number;
};

type State = {
  mousePosition: ChartMousePosition;
};

class MarketDepthChartCore extends React.Component<
  MarketDepthChartProps,
  State
> {
  state: State = {
    mousePosition: undefined,
  };

  xScale: ScaleFn = (x: number) => {
    const { xMin, xMax } = this.props.domain;
    return ((x - xMin) / (xMax - xMin)) * this.props.dimensions.availableWidth;
  };

  yScale: ScaleFn = (y: number) => {
    const { yMin, yMax } = this.props.domain;
    const { availableHeight } = this.props.dimensions;
    return availableHeight - ((y - yMin) / (yMax - yMin)) * availableHeight;
  };

  handleMouseLeave = () => {
    this.setState({
      mousePosition: undefined,
    });
  };

  handleMouseMove: MouseEventHandler<SVGSVGElement | HTMLDivElement> =
    event => {
      const { margin } = depthChartStyle;
      const [mouseX] = mousePositionCallback(event);
      const x = mouseX - margin.left;

      const {
        domain: { xMin, xMax },
        dimensions: { availableWidth },
        midPoint,
        data: { bidPoints, askPoints },
      } = this.props;

      if (x <= 0 && x >= availableWidth) {
        return;
      }
      const xValueFull = (x / availableWidth) * (xMax - xMin) + xMin;
      const xValue = xValueFull;
      if (xValue < midPoint) {
        // use bid
        const items = bidPoints.filter(each => {
          return each.x >= xValue;
        });

        if (items.length < 1) {
          return;
        }
        const item = last(items);
        this.setState({
          mousePosition: {
            type: 'bid',
            xValue: item.x,
            cumulativeCount: item.y,
          },
        });
        return;
      }
      // use ask
      const items = askPoints.filter(each => {
        return each.x <= xValue;
      });

      if (items.length < 1) {
        return;
      }
      const item = last(items);
      this.setState({
        mousePosition: {
          type: 'ask',
          xValue: item.x,
          cumulativeCount: item.y,
        },
      });
    };

  handleMouseClick = () => {
    const xValue = this.state.mousePosition?.xValue;
    if (!xValue) {
      return;
    }
    staticDispatch(prefillOrderInputPrice(xValue.toString()));
  };

  render() {
    const {
      baseSymbol,
      quoteSymbol,
      width,
      height,
      data,
      domain: { xMin, xMax, yMin, yMax },
      dimensions: { availableHeight },
      data: { bidPoints, askPoints },
      midPoint,
      clickPrefillPrice,
    } = this.props;
    const { margin } = depthChartStyle;

    if (!data) {
      return null;
    }

    const { mousePosition } = this.state;

    const mouseXScaled = mousePosition
      ? this.xScale(mousePosition.xValue)
      : undefined;
    const mouseYScaled = mousePosition
      ? this.yScale(mousePosition.cumulativeCount)
      : undefined;

    return (
      <div
        style={{
          position: 'relative',
          width,
          height,
          cursor: clickPrefillPrice ? 'pointer' : 'initial',
        }}
        onMouseMove={this.handleMouseMove}
        onMouseLeave={this.handleMouseLeave}
        onClick={clickPrefillPrice ? this.handleMouseClick : undefined}
      >
        <svg width={width} height={height}>
          <defs>
            <clipPath id={DEPTH_CHART_CLIP_AREA_ID}>
              <rect
                x="0"
                y="0"
                width={width - margin.left - margin.right}
                height={height - margin.top - margin.bottom}
              />
            </clipPath>
          </defs>

          <g transform={`translate(${margin.left - 0.5}, ${margin.top - 0.5})`}>
            <BidsAsks
              yMin={yMin}
              xScale={this.xScale}
              yScale={this.yScale}
              points={bidPoints}
              fill="var(--green4)"
              stroke="var(--green4)"
            />
            <BidsAsks
              yMin={yMin}
              xScale={this.xScale}
              yScale={this.yScale}
              points={askPoints}
              fill="var(--red3)"
              stroke="var(--red3)"
            />

            <MarketDepthAxes
              xMin={xMin}
              xMax={xMax}
              yMin={yMin}
              yMax={yMax}
              xScale={this.xScale}
              yScale={this.yScale}
              marketPair={`${baseSymbol}-${quoteSymbol}`}
            />
            {/* Mid-price vertical line */}
            <line
              x1={this.xScale(midPoint)}
              y1="50"
              x2={this.xScale(midPoint)}
              y2={this.yScale(0)}
              stroke="var(--gray2a)"
            />

            <MarketDepthTooltipLine
              mousePosition={this.state.mousePosition}
              xScaled={mouseXScaled}
              yScaled={mouseYScaled}
              yMin={availableHeight}
            />
          </g>
        </svg>
        <MarketDepthTooltip
          mousePosition={this.state.mousePosition}
          xScaled={mouseXScaled}
          yScaled={mouseYScaled}
          offsetX={margin.left}
          offsetY={margin.top}
          baseSymbol={baseSymbol}
          quoteSymbol={quoteSymbol}
        />
      </div>
    );
  }
}

export default function MarketDepthChart(props: MarketDepthChartCoreProps) {
  const { bids, asks, width, height } = props;
  const precalculated = useMemo(() => {
    const midPoint = (head(head(asks)) + head(head(bids))) / 2;

    const { margin } = depthChartStyle;

    const halfWidth = midPoint * 5;
    const left = Math.max(0, midPoint - halfWidth);
    const right = midPoint + (midPoint - left);

    const bidEndIndex = binarySearchDescending(bids, left, head);
    const askEndIndex = binarySearchAscending(asks, right, head);

    const bidPoints = getCumulativePoints(bids.slice(0, bidEndIndex));
    const askPoints = getCumulativePoints(asks.slice(0, askEndIndex));

    if (askPoints.length <= 0 && bidPoints.length <= 0) {
      return;
    }
    const [xMinTemp, xMaxTemp] = [last(bidPoints).x, last(askPoints).x];

    const realHalfWidth = Math.min(xMaxTemp - midPoint, midPoint - xMinTemp);

    const [yMin, yMax] = [
      0,
      Math.max(last(bidPoints).y, last(askPoints).y) * 1.3,
    ];

    const availableWidth = width - margin.left - margin.right;
    const availableHeight = height - margin.top - margin.bottom;

    const xMin = midPoint - realHalfWidth;
    const xMax = midPoint + realHalfWidth;

    return {
      domain: { xMin, xMax, yMin, yMax },
      dimensions: { availableHeight, availableWidth },
      midPoint,
      data: { bidPoints, askPoints },
    };
  }, [width, height, bids, asks]);

  if (!precalculated) {
    return null;
  }

  return (
    <MarketDepthChartCore
      {...props}
      domain={precalculated.domain}
      dimensions={precalculated.dimensions}
      midPoint={precalculated.midPoint}
      data={precalculated.data}
    />
  );
}
